import React from 'react';
import { Alert, Button, Card, Dialog } from '@betareader/design-system';
import styled from 'styled-components';
import Icon from '../AppIcon';
import useAudiobookVoiceSamples, {
  AudiobookVoice,
} from 'src/hooks/useAudiobookVoiceSamples';
import AudiobookGenerationPanel from './audiobookGenerationPanel';

const Controls = styled.div`
  border-top: 1px solid var(--br-border);
  border-bottom: 1px solid var(--br-border);
`;

const ControlHeading = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 16px 0 14px;
`;

const StepNumber = styled.span`
  display: inline-grid;
  width: 22px;
  height: 22px;
  place-items: center;
  border-radius: 50%;
  background: var(--br-raised-surface);
  color: var(--br-muted);
  font-size: 12px;
`;

const ControlRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 18px;
  align-items: end;
  padding-bottom: 15px;

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
    gap: 9px;
  }
`;

const Field = styled.div`
  min-width: 0;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  color: var(--br-muted);
  font-size: 12px;
  font-weight: 600;
`;

const VoiceSelect = styled.select`
  width: 100%;
  height: 39px;
  padding: 0 10px;
  border: 1px solid var(--br-border);
  border-radius: 4px;
  background: var(--br-surface);
  color: var(--br-text);
  font: inherit;
`;

const VoiceMetadata = styled.div`
  margin-top: 8px;
  color: var(--br-muted);
  font-size: 12px;
  line-height: 1.45;
`;

const VoiceOrigin = styled.div`
  margin-bottom: 2px;
  color: var(--br-accent);
`;

const SampleState = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 39px;
  color: var(--br-muted);
  font-size: 12px;
  white-space: nowrap;

  @media (max-width: 580px) {
    min-height: 0;
  }
`;

const SampleAction = styled(Button)`
  min-height: 0;
  margin: 0;
  padding: 0;
  color: var(--br-accent);
  font-size: 12px;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const VoiceChoice = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;
  border-top: 1px solid var(--br-border);

  @media (max-width: 580px) {
    flex-direction: column;
  }
`;

const ChoiceCopy = styled.div`
  color: var(--br-muted);
  font-size: 13px;
`;

const GenerationStatus = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  padding: 17px 0 0;

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
    gap: 5px;
  }
`;

const ProductionStage = styled.section`
  margin-top: 24px;
  padding-top: 22px;
  border-top: 1px solid var(--br-border);
`;

const ModalTextArea = styled.textarea`
  width: 100%;
  min-height: 92px;
  padding: 10px 54px 10px 10px;
  border: 1px solid var(--br-border);
  background: var(--br-surface);
  border-radius: 4px;
  color: var(--br-text);
  font: inherit;
  line-height: 1.4;
  resize: vertical;
`;

const TextAreaWrap = styled.div`
  position: relative;
`;

const CharacterCount = styled.span`
  position: absolute;
  right: 10px;
  bottom: 9px;
  color: var(--br-muted);
  font-size: 11px;
`;

type Props = { bookId?: string; bookTitle?: string };

const cents = (credits: number) => `$${(credits * 0.1).toFixed(2)}`;

const normalizeText = (text: string) => text.replace(/\s+/g, ' ').trim();

const voiceOrigin = (voice?: AudiobookVoice) => {
  const origin = voice?.originLanguage;
  if (!origin) return null;
  const language = origin.region
    ? `${origin.name} (${origin.region})`
    : origin.name;
  return origin.accent
    ? `${language} · ${origin.accent} accent`
    : language;
};

const AudiobookVoiceSamplePanel: React.FC<Props> = ({ bookId, bookTitle }) => {
  const {
    data,
    loading,
    error,
    render,
    choose,
    getStreamUrl,
    deliveries,
    deliveryError,
    getDeliveryDownload,
  } = useAudiobookVoiceSamples(bookId);
  const [selectedVoiceId, setSelectedVoiceId] = React.useState('');
  const [sampleModalVoice, setSampleModalVoice] = React.useState<AudiobookVoice | null>(null);
  const [sampleText, setSampleText] = React.useState('');
  const [sampleTextError, setSampleTextError] = React.useState<string | null>(null);
  const [playing, setPlaying] = React.useState<string | null>(null);
  const [playUrl, setPlayUrl] = React.useState<string | null>(null);
  const [attested, setAttested] = React.useState(false);
  const [downloading, setDownloading] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!data?.voices?.length) return;
    if (!data.voices.some(voice => voice.id === selectedVoiceId)) {
      const preferred = data.preferredVoiceId &&
        data.voices.some(voice => voice.id === data.preferredVoiceId)
        ? data.preferredVoiceId
        : data.voices[0].id;
      setSelectedVoiceId(preferred);
    }
  }, [data?.voices, data?.preferredVoiceId, selectedVoiceId]);

  if (loading) return <div role="status" aria-busy="true" style={{ marginTop: 40 }}>Loading voices…</div>;
  if (error) return <Alert variant="error">{error.message}</Alert>;
  if (!data) return null;

  const selectedVoice = data.voices.find(voice => voice.id === selectedVoiceId) || data.voices[0];
  const selectedSample = selectedVoice?.sample;
  const sampleReady = selectedSample?.status === 'ready';
  const sampleInProgress = ['queued', 'generating'].includes(selectedSample?.status || '');
  const selectedByAuthor = data.preferredVoiceId === selectedVoice?.id;
  const sampleBusy = render.isPending;
  const selectBusy = choose.isPending;
  const maxSampleChars = data.maxSampleChars || 500;
  const selectedVoiceOrigin = voiceOrigin(selectedVoice);

  const play = async (sampleId: string) => {
    setPlaying(sampleId);
    setPlayUrl(null);
    try {
      const result = await getStreamUrl(sampleId);
      setPlayUrl(result.url);
    } finally {
      setPlaying(null);
    }
  };

  const download = async (path: string) => {
    setDownloading(path);
    try {
      const result = await getDeliveryDownload(path);
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(null);
    }
  };

  const openSampleModal = () => {
    if (!selectedVoice || !data.openingSentence) return;
    setSampleText(data.openingSentence);
    setSampleTextError(null);
    setSampleModalVoice(selectedVoice);
  };

  const submitSample = () => {
    if (!sampleModalVoice) return;
    const normalized = normalizeText(sampleText);
    if (!normalized || normalized.length > maxSampleChars) {
      setSampleTextError(`Enter between 1 and ${maxSampleChars} characters.`);
      return;
    }
    render.mutate({ voiceId: sampleModalVoice.id, sampleText });
    setSampleModalVoice(null);
  };

  return (
    <div data-testid="audiobook-voice-sample-panel">
      <h2 style={{ marginBottom: 6 }}><Icon name="headphones" aria-hidden="true" /> Audiobook</h2>
      <p style={{ color: 'var(--br-muted)', maxWidth: 650 }}>
        Choose the narrator for “{bookTitle || 'your manuscript'}”, then review the exact
        chapter and whole-book cost before you generate. Samples are optional and cost{' '}
        {data.sampleCredits} credit ({cents(data.sampleCredits)}) only after a successful render.
      </p>
      {data.production && (
        <Alert variant="success">
          Production has started with your selected narrator. Completed chapters and book files
          will appear here as they are mastered.
        </Alert>
      )}
      {data.production && deliveryError && <Alert variant="error">{deliveryError.message}</Alert>}
      {data.production && deliveries && (
        <Card data-testid="audiobook-deliveries" style={{ marginTop: 18 }}>
          <h3 style={{ marginTop: 0 }}>Your audiobook downloads</h3>
          <p style={{ color: 'var(--br-muted)' }}>
            {deliveries.exports.status === 'ready'
              ? `Narrated by ${deliveries.narrator}. Your complete audiobook is ready.`
              : 'Chapter downloads appear as they finish. When the narration is complete, open a chapter studio and choose Package audiobook to build the ZIP and M4B.'}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <Button
              variant="secondary"
              disabled={!deliveries.exports.hasZip || downloading === 'exports/zip'}
              aria-busy={downloading === 'exports/zip'}
              onClick={() => download('exports/zip')}
            >Download chapter MP3s (ZIP)</Button>
            <Button
              disabled={!deliveries.exports.hasM4b || downloading === 'exports/m4b'}
              aria-busy={downloading === 'exports/m4b'}
              onClick={() => download('exports/m4b')}
            >Download whole book (M4B)</Button>
          </div>
          {deliveries.chapters.map(chapter => (
            <div key={chapter.partId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid #eee' }}>
              <span>{chapter.number}. {chapter.title}</span>
              <Button
                size="small"
                variant="secondary"
                disabled={chapter.status !== 'ready' || downloading === `chapters/${chapter.partId}`}
                aria-busy={downloading === `chapters/${chapter.partId}`}
                onClick={() => download(`chapters/${chapter.partId}`)}
              >{chapter.status === 'ready' ? 'MP3' : 'Preparing…'}</Button>
            </div>
          ))}
        </Card>
      )}
      {!data.configured && (
        <Alert variant="info">Voice samples are being set up. No credits can be charged yet.</Alert>
      )}
      {render.error && <Alert variant="error">{(render.error as any).message}</Alert>}
      {choose.error && <Alert variant="error">{(choose.error as any).message}</Alert>}

      <Controls>
        <ControlHeading><StepNumber>1</StepNumber> Narration voice</ControlHeading>
        <ControlRow>
          <Field>
            <FieldLabel htmlFor="audiobook-narration-voice">Narration voice</FieldLabel>
            <VoiceSelect
              id="audiobook-narration-voice"
              value={selectedVoice?.id || ''}
              disabled={!selectedVoice || Boolean(data.production)}
              onChange={event => {
                setSelectedVoiceId(event.target.value);
                setAttested(false);
                setPlayUrl(null);
              }}
            >
              {data.voices.map(voice => {
                const sample = voice.sample;
                const status = sample?.status === 'ready'
                  ? 'sample ready'
                  : ['queued', 'generating'].includes(sample?.status || '')
                    ? 'sample generating'
                    : 'no sample yet';
                return <option key={voice.id} value={voice.id}>{voice.displayName} — {status}</option>;
              })}
            </VoiceSelect>
            {selectedVoice && (
              <VoiceMetadata data-testid="audiobook-selected-voice-metadata">
                {selectedVoiceOrigin && (
                  <VoiceOrigin>
                    <Icon name="language" aria-hidden="true" /> {selectedVoiceOrigin}
                  </VoiceOrigin>
                )}
                <div>{selectedVoice.notes}</div>
              </VoiceMetadata>
            )}
          </Field>
          <SampleState>
            {!selectedVoice ? (
              <span>No voices are available yet</span>
            ) : sampleReady ? (
              <>
                <Button variant="secondary" size="small" onClick={() => play(selectedSample!.id)}
                  disabled={playing === selectedSample!.id} aria-busy={playing === selectedSample!.id}>
                  <Icon name="play" aria-hidden="true" /> Play sample
                </Button>
                <span>Sample ready</span>
                <SampleAction variant="ghost" size="small" disabled={sampleBusy || !data.configured} onClick={openSampleModal}>
                  Generate again
                </SampleAction>
              </>
            ) : sampleInProgress ? (
              <span role="status" aria-busy="true">Generating sample…</span>
            ) : !data.openingSentence ? (
              <span>Sample unavailable for this manuscript</span>
            ) : (
              <>
                <span>No sample yet</span>
                <SampleAction variant="ghost" size="small" disabled={sampleBusy || !data.configured} onClick={openSampleModal}>
                  Generate sample
                </SampleAction>
              </>
            )}
          </SampleState>
        </ControlRow>

        {selectedVoice && !data.production && (
          <VoiceChoice>
            <ChoiceCopy>
              <label htmlFor="audiobook-rights">
                <input
                  id="audiobook-rights"
                  type="checkbox"
                  checked={selectedByAuthor ? true : attested}
                  disabled={selectedByAuthor}
                  onChange={event => setAttested(event.target.checked)}
                />{' '}
                I hold the rights to create and download this audiobook.
              </label>
              <div style={{ marginTop: 5 }}>
                Choose this voice to continue. Generating a sample is optional.
              </div>
            </ChoiceCopy>
            <Button
              size="small"
              variant={selectedByAuthor ? 'secondary' : 'primary'}
              disabled={selectedByAuthor || !attested || selectBusy}
              aria-busy={selectBusy}
              onClick={() => choose.mutate(selectedVoice!.id)}
              data-testid={`audiobook-select-${selectedVoice!.id}`}
            >
              {selectedByAuthor ? 'Selected' : 'Choose this voice'}
            </Button>
          </VoiceChoice>
        )}
      </Controls>

      <GenerationStatus>
        <div>
          <strong>Audiobook generation</strong>
          <div style={{ marginTop: 3, color: 'var(--br-muted)', fontSize: 13 }}>
            {selectedByAuthor
              ? 'Your narrator is selected. Review the cost and generate below.'
              : 'Choose a narrator before generating a chapter or the whole book.'}
          </div>
        </div>
        <span style={{ color: 'var(--br-muted)', fontSize: 12 }}>
          {data.production ? 'In production' : selectedByAuthor ? 'Ready to generate' : 'Choose a voice to continue'}
        </span>
      </GenerationStatus>

      {selectedByAuthor && (
        <ProductionStage>
          <AudiobookGenerationPanel
            bookId={bookId}
            contentVersionId={data.contentVersionId}
            bookTitle={bookTitle}
            narratorName={selectedVoice?.displayName}
            embedded
          />
        </ProductionStage>
      )}

      {playUrl && (
        <Card style={{ marginTop: 16 }} data-testid="audiobook-voice-sample-player">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio controls autoPlay src={playUrl} style={{ width: '100%' }} />
        </Card>
      )}

      <Dialog
        open={Boolean(sampleModalVoice)}
        title="Generate a sample"
        onClose={() => { if (!sampleBusy) setSampleModalVoice(null); }}
        actions={
          <>
            <Button variant="secondary" disabled={sampleBusy} onClick={() => setSampleModalVoice(null)}>
              Cancel
            </Button>
            <Button disabled={!data.configured || sampleBusy} aria-busy={sampleBusy} onClick={submitSample}>
              Generate sample · {cents(data.sampleCredits)}
            </Button>
          </>
        }
      >
        <p>
          Preview {sampleModalVoice?.displayName || 'this voice'} with your opening text. You can adjust it up to {maxSampleChars} characters.
        </p>
        <FieldLabel htmlFor="audiobook-sample-text">Sample text</FieldLabel>
        <TextAreaWrap>
          <ModalTextArea
            id="audiobook-sample-text"
            value={sampleText}
            maxLength={maxSampleChars}
            onChange={event => {
              setSampleText(event.target.value);
              setSampleTextError(null);
            }}
          />
          <CharacterCount>{sampleText.length} / {maxSampleChars}</CharacterCount>
        </TextAreaWrap>
        {sampleTextError && <Alert variant="error" style={{ marginTop: 12 }}>{sampleTextError}</Alert>}
      </Dialog>
    </div>
  );
};

export default AudiobookVoiceSamplePanel;
